import { isoZ, z } from 'my-zod'

const giftCardModel = z.object({
  id: z.string().uuid(),
  shortId: z.string().startsWith('GIFT-'),
  outfitterId: z.number(),
  purchasingCustomerOrderId: z.string().uuid().nullable(),
  recipientFirstName: z.string().min(1),
  recipientLastName: z.string().min(1),
  recipientEmail: z.string().min(1).email(),
  senderName: z.string().nullable(),
  encryptedRedemptionCode: z.string().min(1),
  sha256RedemptionCode: z.string().min(64).max(64),
  startingBalance: z.number().int().positive().max(5000_00),
  expirationDate: isoZ.date().nullable(),
  createdDate: isoZ.instant()
})

const createGiftCardRequest = giftCardModel
  .pick({
    id: true,
    outfitterId: true,
    recipientFirstName: true,
    recipientLastName: true,
    recipientEmail: true,
    startingBalance: true,
    senderName: true,
    purchasingCustomerOrderId: true,
    expirationDate: true
  })
  .augment({
    unencryptedRedemptionCode: z
      .string()
      .min(4)
      .refine((code) => !code.startsWith('GIFT-'), 'Redemption code cannot start with "GIFT-"'),
    sendEmailToRecipient: z.boolean()
  })

const updateGiftCardRequest = giftCardModel.pick({
  shortId: true,
  recipientFirstName: true,
  recipientLastName: true,
  recipientEmail: true
})

const giftCardManualValueAdjustmentRequest = giftCardModel
  .pick({
    shortId: true
  })
  .augment({
    newValue: z.number().int().nonnegative().max(5000_00)
  })

const giftCardSystemView = giftCardModel

const giftCardActivityLogEvent = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('creation'),
    createdDate: isoZ.instant(),
    relatedCustomerOrderShortId: z.string().nullable(),
    amount: z.number()
  }),
  z.object({
    type: z.literal('manuallyAdjustment'),
    createdDate: isoZ.instant(),
    amount: z.number()
  }),
  z.object({
    type: z.literal('redemption'),
    createdDate: isoZ.instant(),
    relatedCustomerOrderShortId: z.string(),
    amount: z.number()
  }),
  z.object({
    type: z.literal('refund'),
    createdDate: isoZ.instant(),
    relatedCustomerOrderShortId: z.string(),
    amount: z.number()
  })
])

export const GiftCardSchemas = {
  giftCardModel,
  createGiftCardRequest,
  updateGiftCardRequest,
  giftCardSystemView,
  giftCardManualValueAdjustmentRequest,
  giftCardActivityLogEvent
}

export declare namespace GiftCardSchemas {
  export type giftCardModel = z.infer<typeof giftCardModel>
  export type giftCardSystemView = z.infer<typeof giftCardSystemView>
  export type createGiftCardRequest = z.infer<typeof createGiftCardRequest>
  export type updateGiftCardRequest = z.infer<typeof updateGiftCardRequest>
  export type giftCardManualValueAdjustmentRequest = z.infer<typeof giftCardManualValueAdjustmentRequest>
  export type giftCardActivityLogEvent = z.infer<typeof giftCardActivityLogEvent>
}
